import React from 'react';
import { AlertTriangle, AlertCircle, XCircle } from 'lucide-react';

interface ExpirationWarningProps {
  expirationDate?: Date;
  className?: string;
  showIcon?: boolean;
  showText?: boolean;
}

const ExpirationWarning: React.FC<ExpirationWarningProps> = ({ 
  expirationDate, 
  className = '',
  showIcon = true,
  showText = true
}) => {
  if (!expirationDate) {
    return null;
  }

  const now = new Date();
  const expiry = new Date(expirationDate);
  const diffTime = expiry.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  let status: 'expired' | 'critical' | 'warning' | 'good' = 'good';
  let color = 'text-green-400';
  let bgColor = 'bg-green-900/30 border-green-500/30';
  let icon = null;
  let text = '';

  if (diffDays < 0) {
    status = 'expired';
    color = 'text-red-400';
    bgColor = 'bg-red-900/30 border-red-500/30';
    icon = <XCircle className="w-4 h-4" />;
    text = `Expired ${Math.abs(diffDays)} days ago`;
  } else if (diffDays <= 10) {
    status = 'critical';
    color = 'text-red-400';
    bgColor = 'bg-red-900/30 border-red-500/30';
    icon = <AlertCircle className="w-4 h-4" />;
    text = `Expires in ${diffDays} days`;
  } else if (diffDays <= 30) {
    status = 'warning';
    color = 'text-yellow-400';
    bgColor = 'bg-yellow-900/30 border-yellow-500/30';
    icon = <AlertTriangle className="w-4 h-4" />;
    text = `Expires in ${diffDays} days`;
  } else if (diffDays <= 60) {
    status = 'warning';
    color = 'text-blue-400';
    bgColor = 'bg-blue-900/30 border-blue-500/30';
    icon = <AlertTriangle className="w-4 h-4" />;
    text = `Expires in ${diffDays} days`;
  }

  if (status === 'good') {
    return null;
  }

  return (
    <div className={`flex items-center space-x-2 px-3 py-1 rounded-md border ${bgColor} ${className}`}>
      {showIcon && icon && (
        <span className={color}>
          {icon}
        </span>
      )}
      {showText && (
        <span className={`text-sm font-medium ${color}`}>
          {text}
        </span>
      )}
    </div>
  );
};

export default ExpirationWarning;
